import React, { useState, useEffect } from 'react';
import CogginsUploadField from '../shared/CogginsUploadField.tsx';
import PageTitle from '../common/PageTitle.tsx';
import HorsePhotoPlaceholder from '../common/HorsePhotoPlaceholder.tsx';
import { AppIcon } from '../../icons/appIcons.tsx';
import { deleteHorseCogginsDocument, uploadHorseCogginsDocument } from '../../utils/horseCareApi.ts';

interface Horse {
  id: string;
  name: string;
  breed?: string;
  age?: number;
  color?: string;
  stallNumber?: string;
  designation: 'personal horse' | 'troop horse' | 'for lease' | 'for sale';
  status: 'In isolation' | 'coming soon' | 'In herd' | 'do not ride';
  assignedRanchGirls?: Array<{
    id: string;
    firstName: string;
    lastName: string;
    family: {
      familyName: string;
    };
  }>;
  ownerFamily?: {
    id: string;
    familyName: string;
  };
  specialNeeds?: string;
  medicalNotes?: string;
  temperament?: string;
  ridingLevel?: string;
  imageUrl?: string;
  acquisitionDate?: string;
  farrierName?: string;
  lastSeenByFarrier?: string;
  lastCoggins?: string;
  lastWormer?: string;
  lastRabies?: string;
  cogginsDocumentUrl?: string;
  cogginsDocumentOriginalName?: string;
  cogginsDocumentMimeType?: string;
  notes?: string;
  isActive: boolean;
}

interface RanchGirl {
  id: string;
  firstName: string;
  lastName: string;
  family: {
    familyName: string;
  };
}

interface Family {
  id: string;
  familyName: string;
}

export default function HorseManagement() {
  const [horses, setHorses] = useState<Horse[]>([]);
  const [ranchGirls, setRanchGirls] = useState<RanchGirl[]>([]);
  const [families, setFamilies] = useState<Family[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterDesignation, setFilterDesignation] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'stallNumber' | 'acquisitionDate' | 'lastSeenByFarrier' | 'lastCoggins'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedHorse, setSelectedHorse] = useState<Horse | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  useEffect(() => {
    fetchHorses();
    fetchRanchGirls();
    fetchFamilies();
  }, []);

  const fetchHorses = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/horses', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setHorses(data.data);
      }
    } catch (error) {
      console.error('Error fetching horses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRanchGirls = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/families', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        const allRanchGirls: RanchGirl[] = [];
        data.data.forEach((family: any) => {
          if (family.ranchGirls) {
            family.ranchGirls.forEach((girl: any) => {
              allRanchGirls.push({
                ...girl,
                family: {
                  familyName: family.familyName
                }
              });
            });
          }
        });
        setRanchGirls(allRanchGirls);
      }
    } catch (error) {
      console.error('Error fetching ranch girls:', error);
    }
  };

  const fetchFamilies = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/families', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setFamilies(data.data);
      }
    } catch (error) {
      console.error('Error fetching families:', error);
    }
  };

  const exportToCSV = () => {
    // Define CSV headers
    const headers = [
      'Name',
      'Breed',
      'Age',
      'Color',
      'Stall Number',
      'Designation',
      'Status',
      'Temperament',
      'Riding Level',
      'Special Needs',
      'Medical Notes',
      'Assigned Ranch Girls',
      'Owner Family',
      'Acquisition Date',
      'Farrier Name',
      'Last Seen by Farrier',
      'Last Coggins',
      'Last Wormer',
      'Last Rabies',
      'Coggins Document',
      'Notes',
      'Active'
    ];

    // Map horses to CSV rows
    const rows = filteredHorses.map(horse => [
      horse.name,
      horse.breed || '',
      horse.age?.toString() || '',
      horse.color || '',
      horse.stallNumber || '',
      horse.designation,
      horse.status,
      horse.temperament || '',
      horse.ridingLevel || '',
      horse.specialNeeds || '',
      horse.medicalNotes || '',
      horse.assignedRanchGirls?.map(rg => `${rg.firstName} ${rg.lastName} (${rg.family.familyName})`).join('; ') || '',
      horse.ownerFamily?.familyName || '',
      horse.acquisitionDate || '',
      horse.farrierName || '',
      horse.lastSeenByFarrier || '',
      horse.lastCoggins || '',
      horse.lastWormer || '',
      horse.lastRabies || '',
      horse.cogginsDocumentOriginalName || horse.cogginsDocumentUrl || '',
      horse.notes || '',
      horse.isActive ? 'Yes' : 'No'
    ]);

    // Convert to CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        const cellStr = String(cell).replace(/"/g, '""');
        return cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n') 
          ? `"${cellStr}"` 
          : cellStr;
      }).join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `horses_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEditHorse = (horse: Horse) => {
    setSelectedHorse(horse);
    setShowEditForm(true);
  };

  const handleCloseForm = () => {
    setShowAddForm(false);
    setShowEditForm(false);
    setSelectedHorse(null);
  };

  const handleFormSuccess = () => {
    handleCloseForm();
    fetchHorses();
  };

  const handleDeleteHorse = async (horseId: string, horseName: string) => {
    if (!window.confirm(`Are you sure you want to delete ${horseName}? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/horses/${horseId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        fetchHorses();
      } else {
        alert(data.message || 'Failed to delete horse');
      }
    } catch (error) {
      console.error('Error deleting horse:', error);
      alert('Failed to delete horse. Please try again.');
    }
  };

  const filteredHorses = horses
    .filter(horse => {
      if (filterDesignation !== 'all' && horse.designation !== filterDesignation) return false;
      if (filterStatus !== 'all' && horse.status !== filterStatus) return false;
      return true;
    })
    .sort((a, b) => {
      const dir = sortDirection === 'asc' ? 1 : -1;

      const compareStrings = (av?: string, bv?: string) => {
        const aEmpty = !av || av.trim() === '';
        const bEmpty = !bv || bv.trim() === '';
        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return 1;
        if (bEmpty) return -1;
        return av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' }) * dir;
      };

      const compareDates = (av?: string, bv?: string) => {
        const at = av ? new Date(av).getTime() : NaN;
        const bt = bv ? new Date(bv).getTime() : NaN;
        const aBad = isNaN(at);
        const bBad = isNaN(bt);
        if (aBad && bBad) return 0;
        if (aBad) return 1;
        if (bBad) return -1;
        return (at - bt) * dir;
      };

      switch (sortBy) {
        case 'stallNumber':
          return compareStrings(a.stallNumber, b.stallNumber);
        case 'acquisitionDate':
          return compareDates(a.acquisitionDate, b.acquisitionDate);
        case 'lastSeenByFarrier':
          return compareDates(a.lastSeenByFarrier, b.lastSeenByFarrier);
        case 'lastCoggins':
          return compareDates(a.lastCoggins, b.lastCoggins);
        case 'name':
        default:
          return compareStrings(a.name, b.name);
      }
    });

  const getDesignationBadge = (designation: string) => {
    const badges = {
      'personal horse': 'bg-purple-100 text-purple-800',
      'troop horse': 'bg-blue-100 text-blue-800',
      'for lease': 'bg-green-100 text-green-800',
      'for sale': 'bg-yellow-100 text-yellow-800'
    };
    return badges[designation as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      'In herd': 'bg-green-100 text-green-800',
      'In isolation': 'bg-red-100 text-red-800',
      'coming soon': 'bg-blue-100 text-blue-800',
      'do not ride': 'bg-yellow-100 text-yellow-800'
    };
    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ggaa-green"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-4 sm:py-6 lg:py-10">
      <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:gap-6">
        <div>
          <PageTitle icon="horse">Horse Management</PageTitle>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Manage all ranch horses</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full">
          <button
            type="button"
            onClick={exportToCSV}
            className="w-full sm:w-auto bg-ggaa-green text-white px-4 py-3 rounded-lg hover:bg-ggaa-green/90 font-medium inline-flex items-center justify-center gap-2 min-h-[44px]"
          >
            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export to CSV
          </button>
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="w-full sm:w-auto btn-primary min-h-[44px] py-3"
          >
            + Add Horse
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Total Horses</p>
              <p className="text-2xl sm:text-3xl font-bold text-ggaa-primary mt-1 sm:mt-2">{horses.length}</p>
            </div>
            <div className="bg-ggaa-light rounded-full p-2 sm:p-3 shrink-0 hidden sm:flex">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-ggaa-green/10 text-ggaa-green">
                <AppIcon name="horse" className="w-6 h-6" />
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600">In Herd</p>
              <p className="text-2xl sm:text-3xl font-bold text-blue-600 mt-1 sm:mt-2">
                {horses.filter(h => h.status === 'In herd').length}
              </p>
            </div>
            <div className="bg-blue-50 rounded-full p-2 sm:p-3 shrink-0 hidden sm:flex">
              <span className="text-2xl sm:text-3xl">✅</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Troop Horses</p>
              <p className="text-2xl sm:text-3xl font-bold text-green-600 mt-1 sm:mt-2">
                {horses.filter(h => h.designation === 'troop horse').length}
              </p>
            </div>
            <div className="bg-green-50 rounded-full p-2 sm:p-3 shrink-0 hidden sm:flex">
              <span className="text-2xl sm:text-3xl">🏇</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 col-span-2 md:col-span-1">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600">For Lease / Sale</p>
              <p className="text-2xl sm:text-3xl font-bold text-purple-600 mt-1 sm:mt-2">
                {horses.filter(h => h.designation === 'for lease' || h.designation === 'for sale').length}
              </p>
            </div>
            <div className="bg-purple-50 rounded-full p-2 sm:p-3 shrink-0 hidden sm:flex">
              <span className="text-2xl sm:text-3xl">🏷️</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Sort */}
      <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Designation
            </label>
            <select
              value={filterDesignation}
              onChange={(e) => setFilterDesignation(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-3 sm:py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-ggaa-green min-h-[44px]"
            >
              <option value="all">All Designations</option>
              <option value="personal horse">Personal Horse</option>
              <option value="troop horse">Troop Horse</option>
              <option value="for lease">For Lease</option>
              <option value="for sale">For Sale</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-3 sm:py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-ggaa-green min-h-[44px]"
            >
              <option value="all">All Statuses</option>
              <option value="In herd">In Herd</option>
              <option value="In isolation">In Isolation</option>
              <option value="coming soon">Coming Soon</option>
              <option value="do not ride">Do Not Ride</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sort By
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="w-full border border-gray-300 rounded-lg px-3 py-3 sm:py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-ggaa-green min-h-[44px]"
            >
              <option value="name">Name</option>
              <option value="stallNumber">Stall Number</option>
              <option value="acquisitionDate">Acquisition Date</option>
              <option value="lastSeenByFarrier">Last Seen by Farrier</option>
              <option value="lastCoggins">Last Coggins</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sort Direction
            </label>
            <button
              type="button"
              onClick={() => setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))}
              className="w-full border border-gray-300 rounded-lg px-3 py-3 sm:py-2 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-ggaa-green text-left flex items-center justify-between min-h-[44px] text-base sm:text-sm"
            >
              <span className="pr-2">
                <span className="sm:hidden">{sortDirection === 'asc' ? 'Ascending ↑' : 'Descending ↓'}</span>
                <span className="hidden sm:inline">
                  {sortDirection === 'asc' ? 'Ascending (A→Z, oldest→newest)' : 'Descending (Z→A, newest→oldest)'}
                </span>
              </span>
              <span className="text-ggaa-green font-bold shrink-0">
                {sortDirection === 'asc' ? '↑' : '↓'}
              </span>
            </button>
          </div>
        </div>
        <p className="mt-3 text-xs sm:text-sm text-gray-500">
          Showing {filteredHorses.length} of {horses.length} horses
        </p>
      </div>

      {/* Horse Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
        {filteredHorses.length === 0 ? (
          <div className="col-span-full text-center py-12 px-4 bg-white rounded-xl shadow-md">
            <p className="text-gray-500 mb-4">
              {horses.length === 0 ? 'No horses yet.' : 'No horses match your filters.'}
            </p>
            {horses.length === 0 && (
              <button
                type="button"
                onClick={() => setShowAddForm(true)}
                className="text-ggaa-green font-medium hover:underline"
              >
                Add your first horse
              </button>
            )}
          </div>
        ) : (
          filteredHorses.map(horse => (
            <div key={horse.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              {/* Horse Image */}
              <div className="h-48 bg-gray-200 relative">
                {horse.imageUrl ? (
                  <img
                    src={horse.imageUrl}
                    alt={horse.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23f0e9d8" width="200" height="200"/></svg>';
                    }}
                  />
                ) : (
                  <HorsePhotoPlaceholder size="md" />
                )}
              </div>

              {/* Horse Info */}
              <div className="p-4 sm:p-6">
                <div className="flex items-start justify-between gap-2 mb-3 sm:mb-4">
                  <div className="min-w-0">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 break-words">{horse.name}</h3>
                    {horse.breed && (
                      <p className="text-sm text-gray-600">{horse.breed}</p>
                    )}
                  </div>
                  {horse.age != null && (
                    <span className="text-sm bg-gray-100 text-gray-700 px-2 py-1 rounded shrink-0">
                      {horse.age}yo
                    </span>
                  )}
                </div>

                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-gray-600">Stall</span>
                    <span className="font-medium text-right">{horse.stallNumber || 'Not assigned'}</span>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <span className="text-gray-600">Owned by</span>
                    <span className="font-medium text-right">
                      {horse.designation === 'personal horse' && horse.ownerFamily
                        ? horse.ownerFamily.familyName
                        : 'GGAA'}
                    </span>
                  </div>

                  <div className="flex items-start justify-between gap-2">
                    <span className="text-gray-600 shrink-0">Assigned to</span>
                    <div className="font-medium text-right min-w-0">
                      {horse.assignedRanchGirls && horse.assignedRanchGirls.length > 0 ? (
                        horse.assignedRanchGirls.map((girl) => (
                          <div key={girl.id} className="break-words">
                            {girl.firstName} {girl.lastName}
                          </div>
                        ))
                      ) : (
                        <span className="text-gray-400">Not assigned</span>
                      )}
                    </div>
                  </div>

                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  <span className={`text-xs px-2 py-1 rounded-full ${getDesignationBadge(horse.designation)}`}>
                    {horse.designation}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(horse.status)}`}>
                    {horse.status}
                  </span>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={() => handleEditHorse(horse)}
                    className="flex-1 bg-ggaa-primary text-white px-4 py-3 rounded-lg hover:bg-ggaa-dark transition-colors text-sm font-medium min-h-[44px]"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteHorse(horse.id, horse.name);
                    }}
                    className="flex-1 sm:flex-none sm:min-w-[44px] px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium min-h-[44px]"
                  >
                    <span className="sm:hidden">Delete</span>
                    <span className="hidden sm:inline">🗑️</span>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Forms */}
      {showAddForm && (
        <AddHorseModal
          onClose={handleCloseForm}
          onSuccess={handleFormSuccess}
        />
      )}

      {showEditForm && selectedHorse && (
        <EditHorseModal
          horse={selectedHorse}
          onClose={handleCloseForm}
          onSuccess={handleFormSuccess}
        />
      )}
      </div>
    </div>
  );
}

// Add Horse Modal Component
function AddHorseModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [formData, setFormData] = useState({
    name: '',
    breed: '',
    age: '',
    color: '',
    stallNumber: '',
    designation: 'troop horse',
    status: 'In herd',
    ownerFamilyId: '',
    specialNeeds: '',
    medicalNotes: '',
    temperament: '',
    ridingLevel: '',
    acquisitionDate: '',
    farrierName: '',
    lastSeenByFarrier: '',
    lastCoggins: '',
    lastWormer: '',
    lastRabies: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [families, setFamilies] = useState<Family[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [cogginsFile, setCogginsFile] = useState<File | null>(null);

  useEffect(() => {
    fetchFamilies();
  }, []);

  const fetchFamilies = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/families', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setFamilies(data.data);
      }
    } catch (error) {
      console.error('Error fetching families:', error);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      const payload = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : undefined,
        ownerFamilyId: formData.ownerFamilyId || undefined,
        acquisitionDate: formData.acquisitionDate || undefined,
        lastSeenByFarrier: formData.lastSeenByFarrier || undefined,
        lastCoggins: formData.lastCoggins || undefined,
        lastWormer: formData.lastWormer || undefined,
        lastRabies: formData.lastRabies || undefined
      };

      const response = await fetch('/api/horses', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        const horseId = data.data?.id;
        if (imageFile && horseId) {
          const imageFormData = new FormData();
          imageFormData.append('horseImage', imageFile);

          await fetch(`/api/horses/${horseId}/upload-image`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: imageFormData
          });
        }
        if (cogginsFile && horseId) {
          await uploadHorseCogginsDocument(horseId, cogginsFile);
        }

        onSuccess();
      } else {
        setError(data.message || 'Failed to create horse');
      }
    } catch (error) {
      console.error('Error creating horse:', error);
      setError(error instanceof Error ? error.message : 'Failed to create horse. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-white rounded-t-xl sm:rounded-xl max-w-4xl w-full max-h-[92vh] sm:max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <AppIcon name="horse" className="w-6 h-6 text-ggaa-green" />
                Add New Horse
              </h3>
              <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Image Upload Section */}
            <div className="mb-6 text-center">
              <div className="inline-block">
                {imagePreview ? (
                  <img src={imagePreview} alt="Horse preview" className="w-32 h-32 rounded-full object-cover mb-2" />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center mb-2">
                    <AppIcon name="photo" className="w-12 h-12 text-gray-400" />
                  </div>
                )}
                <label className="block">
                  <span className="sr-only">Choose horse image</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-ggaa-primary file:text-white hover:file:bg-ggaa-dark"
                  />
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      placeholder="Horse name"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Breed</label>
                      <input
                        type="text"
                        value={formData.breed}
                        onChange={(e) => setFormData(prev => ({ ...prev, breed: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.age}
                        onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                      <input
                        type="text"
                        value={formData.color}
                        onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                        placeholder="Bay, chestnut, etc."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Stall Number</label>
                      <input
                        type="text"
                        value={formData.stallNumber}
                        onChange={(e) => setFormData(prev => ({ ...prev, stallNumber: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Designation <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.designation}
                      onChange={(e) => setFormData(prev => ({ ...prev, designation: e.target.value as 'personal horse' | 'troop horse' | 'for lease' | 'for sale', ownerFamilyId: e.target.value !== 'personal horse' ? '' : prev.ownerFamilyId }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    >
                      <option value="troop horse">Troop Horse</option>
                      <option value="personal horse">Personal Horse</option>
                      <option value="for lease">For Lease</option>
                      <option value="for sale">For Sale</option>
                    </select>
                  </div>

                  {formData.designation === 'personal horse' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Owner Family <span className="text-red-500">*</span>
                      </label>
                      <select
                        required
                        value={formData.ownerFamilyId}
                        onChange={(e) => setFormData(prev => ({ ...prev, ownerFamilyId: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      >
                        <option value="">Select a family</option>
                        {families.map(family => (
                          <option key={family.id} value={family.id}>
                            {family.familyName}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as 'In isolation' | 'coming soon' | 'In herd' | 'do not ride' }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    >
                      <option value="In herd">In Herd</option>
                      <option value="In isolation">In Isolation</option>
                      <option value="coming soon">Coming Soon</option>
                      <option value="do not ride">Do Not Ride</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Temperament</label>
                    <input
                      type="text"
                      value={formData.temperament}
                      onChange={(e) => setFormData(prev => ({ ...prev, temperament: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      placeholder="Calm, energetic, friendly, etc."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Riding Level</label>
                    <input
                      type="text"
                      value={formData.ridingLevel}
                      onChange={(e) => setFormData(prev => ({ ...prev, ridingLevel: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      placeholder="Beginner, intermediate, advanced"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Acquisition Date</label>
                    <input
                      type="date"
                      value={formData.acquisitionDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, acquisitionDate: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Farrier Name</label>
                    <input
                      type="text"
                      value={formData.farrierName}
                      onChange={(e) => setFormData(prev => ({ ...prev, farrierName: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Seen by Farrier</label>
                      <input
                        type="date"
                        value={formData.lastSeenByFarrier}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastSeenByFarrier: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Coggins</label>
                      <input
                        type="date"
                        value={formData.lastCoggins}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastCoggins: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Wormer</label>
                      <input
                        type="date"
                        value={formData.lastWormer}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastWormer: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Rabies</label>
                      <input
                        type="date"
                        value={formData.lastRabies}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastRabies: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                  </div>

                  <CogginsUploadField
                    selectedFile={cogginsFile}
                    onFileChange={setCogginsFile}
                    disabled={loading}
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Special Needs</label>
                    <textarea
                      value={formData.specialNeeds}
                      onChange={(e) => setFormData(prev => ({ ...prev, specialNeeds: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      rows={3}
                      placeholder="Dietary requirements, special care instructions..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Medical Notes</label>
                    <textarea
                      value={formData.medicalNotes}
                      onChange={(e) => setFormData(prev => ({ ...prev, medicalNotes: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      rows={3}
                      placeholder="Medical conditions, medications, vet notes..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      rows={2}
                      placeholder="General notes..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-4">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-6 py-3 sm:py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors min-h-[44px]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto px-6 py-3 sm:py-2 bg-ggaa-primary text-white rounded-lg hover:bg-ggaa-dark transition-colors disabled:opacity-50 min-h-[44px]"
              >
                {loading ? 'Adding...' : 'Add Horse'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Horse Modal Component
function EditHorseModal({ horse, onClose, onSuccess }: { horse: Horse; onClose: () => void; onSuccess: () => void }) {
  const [formData, setFormData] = useState({
    name: horse.name || '',
    breed: horse.breed || '',
    age: horse.age?.toString() || '',
    color: horse.color || '',
    stallNumber: horse.stallNumber || '',
    designation: horse.designation || 'troop horse',
    status: horse.status || 'In herd',
    ownerFamilyId: horse.ownerFamily?.id || '',
    specialNeeds: horse.specialNeeds || '',
    medicalNotes: horse.medicalNotes || '',
    temperament: horse.temperament || '',
    ridingLevel: horse.ridingLevel || '',
    acquisitionDate: horse.acquisitionDate ? horse.acquisitionDate.split('T')[0] : '',
    farrierName: horse.farrierName || '',
    lastSeenByFarrier: horse.lastSeenByFarrier ? horse.lastSeenByFarrier.split('T')[0] : '',
    lastCoggins: horse.lastCoggins ? horse.lastCoggins.split('T')[0] : '',
    lastWormer: horse.lastWormer ? horse.lastWormer.split('T')[0] : '',
    lastRabies: horse.lastRabies ? horse.lastRabies.split('T')[0] : '',
    notes: horse.notes || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [families, setFamilies] = useState<Family[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(horse.imageUrl || null);
  const [cogginsFile, setCogginsFile] = useState<File | null>(null);
  const [cogginsDocumentUrl, setCogginsDocumentUrl] = useState<string | null>(horse.cogginsDocumentUrl || null);
  const [cogginsDocumentOriginalName, setCogginsDocumentOriginalName] = useState<string | null>(
    horse.cogginsDocumentOriginalName || null
  );
  const [removeCogginsPending, setRemoveCogginsPending] = useState(false);

  useEffect(() => {
    fetchFamilies();
  }, []);

  const fetchFamilies = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/families', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setFamilies(data.data);
      }
    } catch (error) {
      console.error('Error fetching families:', error);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      const payload = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : undefined,
        ownerFamilyId: formData.ownerFamilyId || undefined,
        acquisitionDate: formData.acquisitionDate || undefined,
        lastSeenByFarrier: formData.lastSeenByFarrier || null,
        lastCoggins: formData.lastCoggins || null,
        lastWormer: formData.lastWormer || null,
        lastRabies: formData.lastRabies || null
      };

      const response = await fetch(`/api/horses/${horse.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        if (imageFile) {
          const imageFormData = new FormData();
          imageFormData.append('horseImage', imageFile);

          await fetch(`/api/horses/${horse.id}/upload-image`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: imageFormData
          });
        }
        if (cogginsFile) {
          await uploadHorseCogginsDocument(horse.id, cogginsFile);
        }

        onSuccess();
      } else {
        setError(data.message || 'Failed to update horse');
      }
    } catch (error) {
      console.error('Error updating horse:', error);
      setError(error instanceof Error ? error.message : 'Failed to update horse. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCoggins = async () => {
    if (!window.confirm('Remove the attached Coggins document?')) return;
    setRemoveCogginsPending(true);
    setError('');
    try {
      await deleteHorseCogginsDocument(horse.id);
      setCogginsDocumentUrl(null);
      setCogginsDocumentOriginalName(null);
      setCogginsFile(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove Coggins document');
    } finally {
      setRemoveCogginsPending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-white rounded-t-xl sm:rounded-xl max-w-4xl w-full max-h-[92vh] sm:max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <AppIcon name="horse" className="w-6 h-6 text-ggaa-green" />
                Edit Horse
              </h3>
              <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Image Upload Section */}
            <div className="mb-6 text-center">
              <div className="inline-block">
                {imagePreview ? (
                  <img src={imagePreview} alt="Horse preview" className="w-32 h-32 rounded-full object-cover mb-2" />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center mb-2">
                    <AppIcon name="photo" className="w-12 h-12 text-gray-400" />
                  </div>
                )}
                <label className="block">
                  <span className="sr-only">Choose horse image</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-ggaa-primary file:text-white hover:file:bg-ggaa-dark"
                  />
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      placeholder="Horse name"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Breed</label>
                      <input
                        type="text"
                        value={formData.breed}
                        onChange={(e) => setFormData(prev => ({ ...prev, breed: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.age}
                        onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                      <input
                        type="text"
                        value={formData.color}
                        onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                        placeholder="Bay, chestnut, etc."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Stall Number</label>
                      <input
                        type="text"
                        value={formData.stallNumber}
                        onChange={(e) => setFormData(prev => ({ ...prev, stallNumber: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Designation <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.designation}
                      onChange={(e) => setFormData(prev => ({ ...prev, designation: e.target.value as 'personal horse' | 'troop horse' | 'for lease' | 'for sale', ownerFamilyId: e.target.value !== 'personal horse' ? '' : prev.ownerFamilyId }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    >
                      <option value="troop horse">Troop Horse</option>
                      <option value="personal horse">Personal Horse</option>
                      <option value="for lease">For Lease</option>
                      <option value="for sale">For Sale</option>
                    </select>
                  </div>

                  {formData.designation === 'personal horse' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Owner Family <span className="text-red-500">*</span>
                      </label>
                      <select
                        required
                        value={formData.ownerFamilyId}
                        onChange={(e) => setFormData(prev => ({ ...prev, ownerFamilyId: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      >
                        <option value="">Select a family</option>
                        {families.map(family => (
                          <option key={family.id} value={family.id}>
                            {family.familyName}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as 'In isolation' | 'coming soon' | 'In herd' | 'do not ride' }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    >
                      <option value="In herd">In Herd</option>
                      <option value="In isolation">In Isolation</option>
                      <option value="coming soon">Coming Soon</option>
                      <option value="do not ride">Do Not Ride</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Temperament</label>
                    <input
                      type="text"
                      value={formData.temperament}
                      onChange={(e) => setFormData(prev => ({ ...prev, temperament: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      placeholder="Calm, energetic, friendly, etc."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Riding Level</label>
                    <input
                      type="text"
                      value={formData.ridingLevel}
                      onChange={(e) => setFormData(prev => ({ ...prev, ridingLevel: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      placeholder="Beginner, intermediate, advanced"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Acquisition Date</label>
                    <input
                      type="date"
                      value={formData.acquisitionDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, acquisitionDate: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Farrier Name</label>
                    <input
                      type="text"
                      value={formData.farrierName}
                      onChange={(e) => setFormData(prev => ({ ...prev, farrierName: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Seen by Farrier</label>
                      <input
                        type="date"
                        value={formData.lastSeenByFarrier}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastSeenByFarrier: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Coggins</label>
                      <input
                        type="date"
                        value={formData.lastCoggins}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastCoggins: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Wormer</label>
                      <input
                        type="date"
                        value={formData.lastWormer}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastWormer: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Rabies</label>
                      <input
                        type="date"
                        value={formData.lastRabies}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastRabies: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                  </div>

                  <CogginsUploadField
                    horseId={horse.id}
                    existingUrl={cogginsDocumentUrl}
                    existingFileName={cogginsDocumentOriginalName}
                    selectedFile={cogginsFile}
                    onFileChange={setCogginsFile}
                    onRemoveExisting={cogginsDocumentUrl ? handleRemoveCoggins : undefined}
                    removeExistingPending={removeCogginsPending}
                    disabled={loading}
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Special Needs</label>
                    <textarea
                      value={formData.specialNeeds}
                      onChange={(e) => setFormData(prev => ({ ...prev, specialNeeds: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      rows={3}
                      placeholder="Dietary requirements, special care instructions..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Medical Notes</label>
                    <textarea
                      value={formData.medicalNotes}
                      onChange={(e) => setFormData(prev => ({ ...prev, medicalNotes: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      rows={3}
                      placeholder="Medical conditions, medications, vet notes..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      rows={2}
                      placeholder="General notes..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-4">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-6 py-3 sm:py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors min-h-[44px]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto px-6 py-3 sm:py-2 bg-ggaa-primary text-white rounded-lg hover:bg-ggaa-dark transition-colors disabled:opacity-50 min-h-[44px]"
              >
                {loading ? 'Updating...' : 'Update Horse'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
